import { IAdvancedFormConfig } from '~shared/modules/advanced-form/models/advanced-form.model';
import { FIELD_TYPES } from '~shared/modules/advanced-form/constants/field-types.constant';

import {
  TIER_TWO_REGISTRATION_VALIDATION_RULES_GERMANY,
  TIER_TWO_REGISTRATION_VALIDATION_RULES_SPAIN,
  TIER_TWO_REGISTRATION_VALIDATION_RULES_ITALY,
  TIER_TWO_REGISTRATION_VALIDATION_RULES_PORTUGAL,
  TIER_TWO_REGISTRATION_VALIDATION_RULES_NETHERLANDS,
  TIER_TWO_REGISTRATION_VALIDATION_RULES_FRANCE,
} from '~shared/constants';

import { CountryCode, CountryValueForDropDown } from '~shared/constants/country-code.enum';
import { IAdvancedFormField } from '~shared/modules/advanced-form/models/advanced-form-fields.model';
import { onAddressOptionClick, searchAddress, trackAddressFieldClick } from '~server/utils/auth.utils';
import { environment } from '~env/environment';

const countryLabel = 'REGULAR.FIELDS.COUNTRY.LABEL';
const tierTwoVATNumberLabel = 'REGULAR.FIELDS.VAT_NUMBER_FOR_TIER_TWO_SIGNUP.LABEL';

const ACTIONS_LIST_REGISTRATION = [
  {
    type: 'submit',
    text: 'PAGES.AUTH.PASSWORDLESS_SIGNUP.ADD_BUSINESS_DETAILS.BUTTON',
  },
];

const VAT_NUMBER = {
  name: 'vatNumber',
  label: tierTwoVATNumberLabel,
  placeholder: 'IT12345678910',
  hasRequiredMark: true,
};

const KVK_NUMBER = {
  name: 'kvkNumber',
  label: 'REGULAR.FIELDS.KVK_NUMBER_FOR_TIER_TWO_SIGNUP.LABEL',
  placeholder: '12345678910',
  hasRequiredMark: true,
};

const SIREN_NUMBER = {
  name: 'sirenNumber',
  label: 'REGULAR.FIELDS.SIREN_NUMBER_FOR_TIER_TWO_SIGNUP.LABEL',
  placeholder: '12345678910',
  hasRequiredMark: true,
};

const VAT_NUMBER_PORTUGAL = {
  ...VAT_NUMBER,
  placeholder: 'PT123456789',
};

const VAT_NUMBER_NETHERLANDS = {
  ...VAT_NUMBER,
  placeholder: 'NL123456789B01',
};

const PREFILLED_VAT_NUMBER = {
  ...VAT_NUMBER,
};

const PREFILLED_KVK_NUMBER = {
  ...KVK_NUMBER,
};

const PREFILLED_SIREN_NUMBER = {
  ...SIREN_NUMBER,
};

const FULL_NAME = [
  {
    name: 'firstName',
    label: 'REGULAR.FIELDS.FIRST_NAME.LABEL',
    hasRequiredMark: true,
  },
  {
    name: 'lastName',
    label: 'REGULAR.FIELDS.LAST_NAME.LABEL',
    hasRequiredMark: true,
  },
];

const SPECIAL_FIELDS_FOR_TIER_TWO_SIGN_UP_ITALY = [
  {
    name: 'legalElectronicInvoiceId',
    label: 'REGULAR.FIELDS.E_INVOICE_NUMBER.LABEL',
    hasRequiredMark: false,
  },
  {
    name: 'legalEmailAddress',
    label: 'REGULAR.FIELDS.PEC_EMAIL.LABEL',
    hasRequiredMark: false,
  },
];

const PROVINCE_FIELD_FOR_ITALY_LEGAL_ADDRESS = {
  name: 'legalAddressProvince',
  label: 'REGULAR.FIELDS.PROVINCE.LABEL',
  placeholder: 'REGULAR.FIELDS.PROVINCE.PLACEHOLDER',
  hasRequiredMark: true,
};

const COUNTRY_DROP_DOWN: IAdvancedFormField = {
  id: 'country-field',
  name: 'legalAddressCountry',
  label: countryLabel,
  hasRequiredMark: true,
  type: 'select',
  options: [
    { value: CountryValueForDropDown.Germany, label: 'REGULAR.FIELDS.COUNTRY_GERMANY.LABEL' },
    { value: CountryValueForDropDown.Spain, label: 'REGULAR.FIELDS.COUNTRY_SPAIN.LABEL' },
    { value: CountryValueForDropDown.Italy, label: 'REGULAR.FIELDS.COUNTRY_ITALY.LABEL' },
    { value: CountryValueForDropDown.Portugal, label: 'REGULAR.FIELDS.COUNTRY_PORTUGAL.LABEL' },
    { value: CountryValueForDropDown.Netherlands, label: 'REGULAR.FIELDS.COUNTRY_NETHERLANDS.LABEL' },
    { value: CountryValueForDropDown.France, label: 'REGULAR.FIELDS.COUNTRY_FRANCE.LABEL' },
  ],
};

const COUNTRY_TEXT_FIELD: IAdvancedFormField = {
  name: 'legalAddressCountry',
  label: countryLabel,
  hasRequiredMark: true,
};

const TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST: IAdvancedFormField[] = [
  {
    name: 'legalAddressStreet',
    label: 'REGULAR.FIELDS.STREET',
    hasRequiredMark: true,
  },
  {
    name: 'legalAddressBuildingNumber',
    label: 'REGULAR.FIELDS.HOUSE_NUMBER',
    hasRequiredMark: true,
  },
];

const TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST: IAdvancedFormField[] = [
  {
    id: 'address-search-field',
    name: 'searchAddress',
    label: 'REGULAR.FIELDS.ADDRESS_SEARCH.LABEL',
    placeholder: 'REGULAR.FIELDS.ADDRESS_SEARCH.PLACEHOLDER',
    getItemPrefixUrl: () => `${environment.cdnBaseUrl()}/images/icons/location.svg`,
    type: FIELD_TYPES.autocomplete,
    search: searchAddress,
    onOptionClick: onAddressOptionClick,
    onFocus: trackAddressFieldClick,
  },
  {
    id: 'street-field',
    name: 'legalAddressStreet',
    label: 'REGULAR.FIELDS.STREET',
    hasRequiredMark: true,
  },
  {
    id: 'building-number-field',
    name: 'legalAddressBuildingNumber',
    label: 'REGULAR.FIELDS.HOUSE_NUMBER',
    hasRequiredMark: true,
  },
];

const TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST_FRANCE: IAdvancedFormField[] = [
  {
    name: 'legalAddressStreet',
    label: 'REGULAR.FIELDS.STREET',
    hasRequiredMark: true,
  },
  {
    name: 'legalAddressBuildingNumber',
    label: 'REGULAR.FIELDS.HOUSE_NUMBER',
    hasRequiredMark: false,
  },
];

const CITY_FOR_LEGAL_ADDRESS: IAdvancedFormField = {
  id: 'city-field',
  name: 'legalAddressCity',
  label: 'REGULAR.FIELDS.CITY.LABEL',
  hasRequiredMark: true,
};

const PRE_FILLED_CITY_FOR_LEGAL_ADDRESS: IAdvancedFormField = {
  ...CITY_FOR_LEGAL_ADDRESS,
};

const POSTAL_CODE_FOR_LEGAL_ADDRESS: IAdvancedFormField = {
  id: 'postal-code-field',
  name: 'legalAddressPostalCode',
  label: 'REGULAR.FIELDS.POSTAL_CODE.LABEL',
  hasRequiredMark: true,
};

export const SAVE_LEGAL_ADDRESS_AS_SHIPPING_AND_BILLING_CHECKBOX = [
  {
    name: 'saveLegalAddressAsShippingAndBilling',
    label: 'PAGES.AUTH.REGISTER.SAVE.LEGAL_ADDRESS',
    type: FIELD_TYPES.checkbox,
    hasRequiredMark: false,
  },
];

const BUSINESS_NAME = {
  name: 'businessName',
  label: 'REGULAR.FIELDS.BUSINESS.NAME.LABEL',
  hasRequiredMark: true,
};

const BUSINESS_NAME_ITALY = {
  ...BUSINESS_NAME,
  placeholder: 'REGULAR.FIELDS.BUSINESS.NAME.PLACEHOLDER',
  helpText: 'REGULAR.FIELDS.BUSINESS.NAME.HINT',
};

const PREFILLED_FIELDS = [
  PREFILLED_VAT_NUMBER,
  {
    ...BUSINESS_NAME,
  },
];

const PREFILLED_FIELDS_NETHERLANDS = [
  PREFILLED_KVK_NUMBER,
  {
    ...BUSINESS_NAME,
  },
];

const PREFILLED_FIELDS_FRANCE = [
  PREFILLED_SIREN_NUMBER,
  {
    ...BUSINESS_NAME,
  },
];

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_GERMANY: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_GERMANY(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_SPAIN: (autocompleteAddress: boolean) => IAdvancedFormConfig = (
  autocompleteAddress
) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_SPAIN(),
  formSectionList: [
    {
      fieldList: [
        ...FULL_NAME,
        BUSINESS_NAME,
        {
          name: 'taxNumber',
          label: 'REGULAR.FIELDS.SPANISH_TAX_NUMBER.LABEL',
          hasRequiredMark: true,
        },
      ],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_PORTUGAL: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_PORTUGAL(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        VAT_NUMBER_PORTUGAL,
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_NETHERLANDS: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_NETHERLANDS(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        VAT_NUMBER_PORTUGAL,
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_FRANCE: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_FRANCE(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_PORTUGAL: () => IAdvancedFormConfig = () => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_PORTUGAL(),
  formSectionList: [
    {
      fieldList: [...PREFILLED_FIELDS, ...FULL_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST,
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        PRE_FILLED_CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_TEXT_FIELD,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_NETHERLANDS: () => IAdvancedFormConfig =
  () => ({
    validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_NETHERLANDS(),
    formSectionList: [
      {
        fieldList: [...PREFILLED_FIELDS_NETHERLANDS, VAT_NUMBER_NETHERLANDS, ...FULL_NAME],
      },
      {
        title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
        fieldList: [
          ...TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST,
          POSTAL_CODE_FOR_LEGAL_ADDRESS,
          PRE_FILLED_CITY_FOR_LEGAL_ADDRESS,
          COUNTRY_TEXT_FIELD,
        ],
      },
    ],
    actionList: ACTIONS_LIST_REGISTRATION,
  });

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_NETHERLANDS_SHORT: () => IAdvancedFormConfig =
  () => ({
    validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_NETHERLANDS(),
    formSectionList: [
      {
        fieldList: [VAT_NUMBER_NETHERLANDS, ...FULL_NAME],
      },
    ],
    actionList: ACTIONS_LIST_REGISTRATION,
  });

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_FRANCE: () => IAdvancedFormConfig = () => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_FRANCE(),
  formSectionList: [
    {
      fieldList: [...PREFILLED_FIELDS_FRANCE, ...FULL_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST_FRANCE,
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        PRE_FILLED_CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_TEXT_FIELD,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_FRANCE_SHORT: () => IAdvancedFormConfig =
  () => ({
    validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_FRANCE(),
    formSectionList: [
      {
        fieldList: [...FULL_NAME],
      },
    ],
    actionList: ACTIONS_LIST_REGISTRATION,
  });

export const ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_ITALY: (autocompleteAddress: boolean) => IAdvancedFormConfig = (
  autocompleteAddress
) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_ITALY(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME_ITALY, ...SPECIAL_FIELDS_FOR_TIER_TWO_SIGN_UP_ITALY],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        VAT_NUMBER,
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        PROVINCE_FIELD_FOR_ITALY_LEGAL_ADDRESS,
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_ITALY: () => IAdvancedFormConfig = () => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_ITALY(),
  formSectionList: [
    {
      fieldList: [...PREFILLED_FIELDS, ...FULL_NAME, ...SPECIAL_FIELDS_FOR_TIER_TWO_SIGN_UP_ITALY],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST,
        PROVINCE_FIELD_FOR_ITALY_LEGAL_ADDRESS,
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        PRE_FILLED_CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_TEXT_FIELD,
      ],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_WITH_ADDRESS_SAVE_AS_SHIPPING_AND_BILLING_GERMANY: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_GERMANY(),
  formSectionList: [
    {
      fieldList: [...FULL_NAME, BUSINESS_NAME],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
        ...SAVE_LEGAL_ADDRESS_AS_SHIPPING_AND_BILLING_CHECKBOX,
      ] as IAdvancedFormField[],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const ADVANCED_FORM_CONFIG_WITH_ADDRESS_SAVE_AS_SHIPPING_AND_BILLING_SPAIN: (
  autocompleteAddress: boolean
) => IAdvancedFormConfig = (autocompleteAddress) => ({
  validationRules: TIER_TWO_REGISTRATION_VALIDATION_RULES_SPAIN(),
  formSectionList: [
    {
      fieldList: [
        ...FULL_NAME,
        BUSINESS_NAME,
        {
          name: 'taxNumber',
          label: 'REGULAR.FIELDS.SPANISH_TAX_NUMBER.LABEL',
          hasRequiredMark: true,
        },
      ],
    },
    {
      title: 'REGULAR.FIELDS.TIER_TWO_REGISTRATION.LEGAL_ADDRESS',
      fieldList: [
        ...(autocompleteAddress
          ? TIER_TWO_SIGNUP_LEGAL_ADDRESS_AUTOCOMPLETE_FIELD_LIST
          : TIER_TWO_SIGNUP_LEGAL_ADDRESS_FIELD_LIST),
        POSTAL_CODE_FOR_LEGAL_ADDRESS,
        CITY_FOR_LEGAL_ADDRESS,
        COUNTRY_DROP_DOWN,
        ...SAVE_LEGAL_ADDRESS_AS_SHIPPING_AND_BILLING_CHECKBOX,
      ] as IAdvancedFormField[],
    },
  ],
  actionList: ACTIONS_LIST_REGISTRATION,
});

export const COUNTRY_BASED_FORM_CONFIG = ({ autocompleteAddress = false } = {}) => ({
  [CountryCode.Germany]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_GERMANY(autocompleteAddress),
  [CountryCode.Spain]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_SPAIN(autocompleteAddress),
  [CountryCode.Italy]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_ITALY(autocompleteAddress),
  [CountryCode.Portugal]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_PORTUGAL(autocompleteAddress),
  [CountryCode.Netherlands]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_NETHERLANDS(autocompleteAddress),
  [CountryCode.France]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_FRANCE(autocompleteAddress),
});

export const SAVE_ADDRESS_COUNTRY_BASED_FORM_CONFIG = ({ autocompleteAddress = false } = {}) => ({
  [CountryCode.Germany]: ADVANCED_FORM_CONFIG_WITH_ADDRESS_SAVE_AS_SHIPPING_AND_BILLING_GERMANY(autocompleteAddress),
  [CountryCode.Spain]: ADVANCED_FORM_CONFIG_WITH_ADDRESS_SAVE_AS_SHIPPING_AND_BILLING_SPAIN(autocompleteAddress),
  [CountryCode.Italy]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_ITALY(autocompleteAddress),
  [CountryCode.Portugal]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_PORTUGAL(autocompleteAddress),
  [CountryCode.Netherlands]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_NETHERLANDS(autocompleteAddress),
  [CountryCode.France]: ADVANCED_FORM_CONFIG_TIER_BUSINESS_DETAILS_FRANCE(autocompleteAddress),
});

export const PREFILLED_FIELDS_CONFIG = () => ({
  [CountryCode.Italy]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_ITALY(),
  [CountryCode.Portugal]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_PORTUGAL(),
  [CountryCode.Netherlands]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_NETHERLANDS(),
  [CountryCode.France]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_FRANCE(),
});

export const PREFILLED_FIELDS_CONFIG_SHORT = () => ({
  ...PREFILLED_FIELDS_CONFIG(),
  [CountryCode.Netherlands]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_NETHERLANDS_SHORT(),
  [CountryCode.France]: ADVANCED_FORM_CONFIG_BUSINESS_DETAILS_WITH_PREFILLED_DETAILS_FRANCE_SHORT(),
});

export const IS_SAVE_LEGAL_ADDRESS_AS_BILLING_AND_SHIPPING = {
  [CountryCode.Germany]: true,
  [CountryCode.Spain]: true,
  [CountryCode.Portugal]: true,
  [CountryCode.Italy]: true,
  [CountryCode.Netherlands]: true,
  [CountryCode.France]: true,
};

export const COUNTRY_WITH_VAT_NUMBER_VALIDATION = {
  [CountryCode.Italy]: true,
  [CountryCode.Portugal]: true,
};
